import type Phaser from "phaser";
import type { OnlineMatch } from "../net/online-match";
import {
	CLASSIC_RULES,
	CTF_CAPTURES_TO_WIN,
	cloneRules,
	type GameMode,
	KOTH_POINTS_TO_WIN,
	loadMap,
	type MapDef,
	type MatchResult,
	type MatchRules,
	MODERN_RULES,
	RULE_KEYS,
	SIM_VERSION,
	SURVIVAL_2008_ASTEROID_POINTS,
	SURVIVAL_2008_BONUS_EVERY,
	sameRules,
	TICK_MS,
	type WorldState,
} from "../sim";
import type { AiDifficulty } from "../sim/ai/difficulty";
import { customMapProblems } from "../sim/maps";
import { menuScreen, pageScreen } from "../ui/components";
import { el } from "../ui/dom";
import { helpContent } from "../ui/help-page";
import { highscoreTable } from "../ui/highscores-page";
import { mapEditor } from "../ui/map-editor";
import { drawMapPreview, type PreviewImages } from "../ui/map-preview";
import { livesOf, pilotLabel } from "../ui/match-hud";
import { replaysContent } from "../ui/replays-page";
import { RULE_TEXT } from "../ui/rule-labels";
import { rulesEditor } from "../ui/rules-editor";
import type { Screen, ScreenManager } from "../ui/screen-manager";
import { settingsContent } from "../ui/settings-page";
import { type ImageKey, MAPS, type MapKey } from "./assets";
import {
	blankMap,
	deleteCustomMap,
	exportMap,
	importMap,
	loadCustomMaps,
	MAX_CUSTOM_MAPS,
	mapFileName,
	newMapId,
	type StoredMap,
	saveCustomMap,
} from "./custom-maps";
import { setResolutionMode, toggleFullscreen } from "./display";
import {
	boardLabel,
	cleanName,
	HIGHSCORE_BOARDS,
	type HighscoreBoard,
	highscoreBoard,
	insertEntry,
	isScoreBoard,
	loadHighscores,
	loadLastName,
	NAME_MAX_LENGTH,
	qualifies,
	saveHighscores,
	saveLastName,
} from "./highscores";
import { setMotionPreference } from "./motion";
import { CHANGE_REMATCH, cleanCode, OnlineFlow, REMATCH } from "./online-flow";
import {
	deleteReplay,
	encodeInputs,
	loadReplays,
	parseReplay,
	type Replay,
	replayFileName,
	storeReplay,
} from "./replay";
import type { MatchHooks, MatchScene, MatchSceneData } from "./scenes/match-scene";
import { SceneKey } from "./scenes/scene-keys";
import { loadSettings, type Settings, saveSettings } from "./settings";

/** Emitted on `game.events` by the PreloadScene once every asset is loaded. */
export const ASSETS_READY = "gravity:assets-ready";

type MatchSettings = Omit<MatchSceneData, "hooks">;

/**
 * Top-level game flow: which Phaser scene runs and which UI screens are shown.
 * Menus are DOM screens over an AI-vs-AI attract match; a real match runs in MatchScene with
 * the pause menu (and later game-over screen) layered on top.
 */
export class GameFlow {
	private lastMatch: MatchSettings = { playerCount: 2, mode: "duel" };
	/** Pilot names of the match that just ended (P11.8). */
	private lastNames: string[] = [];
	private pauseScreen: Screen | null = null;
	/** The running match, or null while none runs (or while one is still starting). */
	private current: MatchScene | null = null;
	/** Esc pressed while a match was still starting: pause as soon as it is ready. */
	private pauseWhenReady = false;
	/** Browser Back while a match was still starting: ask to quit as soon as it is ready. */
	private askQuitWhenReady = false;
	/** A history entry pushed while a match runs, so the browser's Back button asks first. */
	private backGuard = false;
	/** The popstate of our own `history.back()` (dropping the guard) is not a Back press. */
	private ignorePop = false;
	private readonly online: OnlineFlow;

	private readonly hooks: MatchHooks = {
		onReady: (scene) => {
			this.current = scene;
			if (this.askQuitWhenReady) this.askQuit(scene);
			else if (this.pauseWhenReady) scene.setPaused(true);
			this.pauseWhenReady = false;
			this.askQuitWhenReady = false;
		},
		onPauseChange: (paused) => (paused ? this.showPauseMenu() : this.hidePauseMenu()),
		onGameOver: (result, world) => this.showGameOver(result, world),
		onReplayEnd: () => this.showReplayEnd(),
	};
	/** The replay of the match that just ended (game over screen: Save replay). */
	private lastReplay: Replay | null = null;

	constructor(
		private readonly game: Phaser.Game,
		private readonly ui: ScreenManager,
	) {
		this.online = new OnlineFlow({
			ui,
			startOnline: (match) => this.startOnline(match),
			showMainMenu: () => this.showMainMenu(),
			chooseMatch: (players, start) =>
				this.modeChoice(
					{ playerCount: Math.min(4, Math.max(2, players)) as 2 | 3 | 4, mode: "duel" },
					(s) =>
						start(
							s.map ?? "classic",
							s.rules ?? cloneRules(CLASSIC_RULES),
							s.customMap,
							s.mode === "koth" || s.mode === "ctf" ? s.mode : "duel",
						),
					"Online room",
				),
			describeSetup: (setup) =>
				[
					this.mapOf(setup).name,
					`${rulesLabel(setup.rules)} rules`,
					setup.mode === "koth"
						? "King of the Hill"
						: setup.mode === "ctf"
							? "Capture the Flag"
							: "",
				]
					.filter(Boolean)
					.join(" · "),
		});
		ui.onEscapeWithoutScreen = () => {
			if (this.current) this.current.setPaused(true);
			else if (document.body.dataset.screen === "match") this.pauseWhenReady = true;
		};
		window.addEventListener("popstate", () => this.onBrowserBack());
		// Closing the tab or reloading during a match: the browser's own "Leave site?" prompt.
		window.addEventListener("beforeunload", (ev) => {
			if (this.inMatch()) ev.preventDefault();
		});
	}

	/**
	 * Entry point once assets are loaded. `?play=1|2` skips the menus into a Modern match
	 * (`&rules=classic` for Classic; e2e tests pin it). `?classic` starts the 2008 game the way
	 * the original exe did: a two-player duel on the classic map with Classic rules, whatever
	 * else the URL says.
	 */
	start(): void {
		const params = new URLSearchParams(window.location.search);
		const classic = params.has("classic");
		const play = classic ? "2" : params.get("play");
		const rules = classic || params.get("rules") === "classic" ? CLASSIC_RULES : MODERN_RULES;
		if (play === "1" || play === "2") {
			const objective = classic ? null : params.get("mode");
			const mode: GameMode =
				play === "1"
					? "survival"
					: objective === "koth" || objective === "ctf"
						? objective
						: "duel";
			const difficulty = classic ? null : params.get("ai");
			const ai: MatchSettings["ai"] =
				play === "2" && (difficulty === "easy" || difficulty === "normal" || difficulty === "hard")
					? [{ player: 1, difficulty }]
					: undefined;
			this.startMatch({
				playerCount: play === "1" ? 1 : 2,
				mode,
				rules: cloneRules(rules),
				...(ai ? { ai } : {}),
			});
		} else {
			this.showMainMenu();
			// Invite link: ?join=CODE
			const join = cleanCode(params.get("join") ?? "");
			if (join.length === 4) this.online.join(join);
		}
	}

	showMainMenu(): void {
		this.releaseBackGuard();
		this.online.reset();
		// An AI-vs-AI match plays behind the menu (attract mode).
		this.pauseScreen = null;
		this.current = null;
		this.pauseWhenReady = false;
		this.askQuitWhenReady = false;
		const data: MatchSceneData = { attract: true };
		const scene = this.game.scene.getScene(SceneKey.Match);
		if (this.game.scene.isActive(SceneKey.Match) && scene) scene.scene.restart(data);
		else this.game.scene.start(SceneKey.Match, data);
		this.ui.reset(this.mainMenu());
		document.body.dataset.screen = "main-menu";
	}

	startMatch(settings: MatchSettings): void {
		this.lastMatch = settings;
		this.ui.reset();
		this.pauseScreen = null;
		this.current = null;
		this.pauseWhenReady = false;
		this.askQuitWhenReady = false;
		const data: MatchSceneData = { ...settings, hooks: this.hooks };
		const running = this.game.scene.getScene(SceneKey.Match);
		if (this.game.scene.isActive(SceneKey.Match) && running) running.scene.restart(data);
		else this.game.scene.start(SceneKey.Match, data);
		document.body.dataset.screen = "match";
		if (!settings.replay) this.armBackGuard();
	}

	/** A live match (local or online) is on screen: not a replay, not over. */
	private inMatch(): boolean {
		return document.body.dataset.screen === "match" && !this.lastMatch.replay;
	}

	private armBackGuard(): void {
		if (this.backGuard) return;
		history.pushState({ gravity: "match" }, "");
		this.backGuard = true;
	}

	private releaseBackGuard(): void {
		if (!this.backGuard) return;
		this.backGuard = false;
		this.ignorePop = true;
		history.back();
	}

	/** Back during a match asks before quitting; after it (game over) it returns to the menu. */
	private onBrowserBack(): void {
		if (this.ignorePop) {
			this.ignorePop = false;
			return;
		}
		if (!this.backGuard) return;
		this.backGuard = false;
		if (!this.inMatch()) {
			this.showMainMenu();
			return;
		}
		this.armBackGuard();
		if (this.current) this.askQuit(this.current);
		else this.askQuitWhenReady = true;
	}

	private askQuit(scene: MatchScene): void {
		scene.setPaused(true);
		if (!this.ui.top?.el.classList.contains("confirm-leave")) this.ui.push(this.quitConfirm());
	}

	/** The "Quit / Leave match?" question of the pause menu. */
	private quitConfirm(): Screen {
		return this.lastMatch.online
			? this.confirmLeave("Leave match?", "You can't rejoin this match once you leave.", "Leave")
			: this.confirmLeave("Quit to menu?", "This match will be lost.", "Quit");
	}

	/** The running version in the corner of menus (not shown during play). */
	private withVersion(screen: Screen): Screen {
		screen.el.append(el("p", { class: "app-version" }, `v${__APP_VERSION__}`));
		return screen;
	}

	/** Starts (or, after a rejoin, restarts) the match scene for an online match. */
	startOnline(match: OnlineMatch): void {
		this.startMatch({ online: match });
	}

	// --- Screens ----------------------------------------------------------

	private mainMenu(): Screen {
		const screen = menuScreen({
			title: "Gravity",
			subtitle: "A gravity dogfight for two pilots",
			className: "main-menu",
			items: [
				{
					label: "Single Player",
					hint: "Asteroid survival or a duel against the computer.",
					onSelect: () => this.ui.push(this.singlePlayerMenu()),
				},
				{
					label: "Two Players",
					hint: "Split-screen on one keyboard (or gamepads): duel, King of the Hill or Capture the Flag.",
					onSelect: () => this.ui.push(this.modeChoice({ playerCount: 2, mode: "duel" })),
				},
				{
					label: "Party (3–4)",
					hint: "Free-for-all for 3 or 4 pilots on one screen: keyboards, gamepads and AI pilots.",
					onSelect: () => this.ui.push(this.partyMenu()),
				},
				{
					label: "Online",
					hint: "Duel a friend over the internet: create a room and share the code.",
					onSelect: () => this.ui.push(this.online.menu()),
				},
				{
					label: "Map editor",
					hint: "Build your own maps and play them in every mode, also online.",
					onSelect: () => this.ui.push(this.mapsScreen()),
				},
				{
					label: "Replays",
					hint: "Watch your last matches again, or open a replay file.",
					onSelect: () => this.ui.push(this.replaysScreen()),
				},
				{
					label: "Highscores",
					hint: "Best asteroid survival runs.",
					onSelect: () =>
						this.ui.push(
							pageScreen(
								"Highscores",
								el(
									"div",
									{},
									...HIGHSCORE_BOARDS.flatMap((board) => [
										el("h2", {}, boardLabel(board)),
										highscoreTable(
											loadHighscores(board),
											-1,
											isScoreBoard(board) ? "Score" : "Asteroids",
										),
									]),
								),
								() => this.ui.pop(),
							),
						),
				},
				{
					label: "Help & Controls",
					hint: "Keys, gamepad buttons and how to play.",
					onSelect: () => this.ui.push(this.helpScreen()),
				},
				{
					label: "Settings",
					hint: "Sound, key bindings and display.",
					onSelect: () => this.ui.push(this.settingsScreen()),
				},
			],
		});
		return this.withVersion(screen);
	}

	private singlePlayerMenu(): Screen {
		return menuScreen({
			title: "Single Player",
			back: () => this.ui.pop(),
			items: [
				{
					label: "Versus Computer",
					hint: "Duel an AI pilot on your keyboard's arrow keys (or a gamepad).",
					onSelect: () => this.ui.push(this.difficultyMenu()),
				},
				{
					label: "Asteroid Survival",
					hint: "One ship against an ever faster asteroid field. Score = asteroids destroyed.",
					onSelect: () => this.ui.push(this.mapChoice({ playerCount: 1, mode: "survival" })),
				},
				{
					label: "Survival · 2008 scoring",
					hint: `The original spec: points for staying alive and ${SURVIVAL_2008_ASTEROID_POINTS} per asteroid, 3 ships, a bonus ship every ${SURVIVAL_2008_BONUS_EVERY} points.`,
					onSelect: () =>
						this.ui.push(
							this.mapChoice({ playerCount: 1, mode: "survival", survivalScoring: "2008" }),
						),
				},
			],
		});
	}

	private difficultyMenu(): Screen {
		const pick = (difficulty: AiDifficulty) => () =>
			this.ui.push(
				this.modeChoice({ playerCount: 2, mode: "duel", ai: [{ player: 1, difficulty }] }),
			);
		return menuScreen({
			title: "Opponent",
			subtitle: "Versus computer",
			back: () => this.ui.pop(),
			items: [
				{ label: "Easy", hint: "Slow to react, shaky aim.", onSelect: pick("easy") },
				{ label: "Normal", hint: "A fair fight.", onSelect: pick("normal") },
				{ label: "Hard", hint: "Quick, precise and hard to shake.", onSelect: pick("hard") },
			],
		});
	}

	/** Party (P11.3): how many pilots, then how many of them are human (AI flies the rest). */
	private partyMenu(): Screen {
		const humans = (pilots: 3 | 4) => () =>
			this.ui.push(
				menuScreen({
					title: "Humans",
					subtitle: `${pilots} pilots · keys: arrows, WASD, IJKL, numpad, or gamepads`,
					back: () => this.ui.pop(),
					items: Array.from({ length: pilots }, (_, i) => pilots - i).map((n) => ({
						label:
							n === pilots ? `All ${n} human` : `${n} human${n > 1 ? "s" : ""} + ${pilots - n} AI`,
						hint:
							n === pilots
								? "Everyone flies."
								: "The computer flies the rest at normal difficulty.",
						onSelect: () =>
							this.ui.push(
								this.modeChoice({
									playerCount: pilots,
									mode: "duel",
									ai: Array.from({ length: pilots - n }, (_, k) => ({
										player: n + k,
										difficulty: "normal" as AiDifficulty,
									})),
								}),
							),
					})),
				}),
			);
		return menuScreen({
			title: "Party",
			subtitle: "Free-for-all on one screen",
			back: () => this.ui.pop(),
			items: [
				{ label: "3 pilots", hint: "Blue, red and green.", onSelect: humans(3) },
				{ label: "4 pilots", hint: "Blue, red, green and yellow.", onSelect: humans(4) },
			],
		});
	}

	/** Duel, King of the Hill or Capture the Flag (P11.2), then the map. */
	private modeChoice(
		base: MatchSettings,
		start: (settings: MatchSettings) => void = (s) => this.startMatch(s),
		subtitle?: string,
	): Screen {
		const pick = (mode: GameMode) => () => {
			const settings = { ...base, mode };
			this.ui.push(this.mapChoice(settings, start, subtitle ?? describeMode(settings)));
		};
		return menuScreen({
			title: "Game mode",
			subtitle: subtitle ?? describeMode(base),
			back: () => this.ui.pop(),
			items: [
				{
					label: "Duel",
					hint: "The classic: the last pilot with ships left wins.",
					onSelect: pick("duel"),
				},
				{
					label: "King of the Hill",
					hint: `Hold the band around the biggest planet alone: a point a second, first to ${KOTH_POINTS_TO_WIN}. Unlimited ships.`,
					onSelect: pick("koth"),
				},
				{
					// Two bases: Capture the Flag is a two-player mode.
					disabled: (base.playerCount ?? 2) > 2,
					label: "Capture the Flag",
					hint: `Bring the enemy flag to your base while yours is home. First to ${CTF_CAPTURES_TO_WIN}; carriers fly slower. Unlimited ships.`,
					onSelect: pick("ctf"),
				},
			],
		});
	}

	private mapChoice(
		base: MatchSettings,
		start: (settings: MatchSettings) => void = (s) => this.startMatch(s),
		subtitle = describeMode(base),
	): Screen {
		const builtIn = (Object.keys(MAPS) as MapKey[]).map((key) => ({
			label: "",
			settings: { map: key } as MatchSettings,
			def: loadMap(this.game.cache.text.get(key) as string, MAPS[key]),
		}));
		// Player-made maps (P11.1) after the built-in ones.
		const custom = loadCustomMaps().map((m) => ({
			label: `★ ${m.map.name}`,
			settings: { map: "classic", customMap: m.map } as MatchSettings,
			def: m.map,
		}));
		const maps = [...builtIn, ...custom];
		const canvas = el("canvas", { class: "map-preview", width: 360, height: 360 });
		canvas.setAttribute("aria-hidden", "true");
		const info = el("p", { class: "map-info" });
		const images = this.previewImages();
		const show = (def: MapDef) => {
			drawMapPreview(canvas, def, images);
			info.textContent = `${def.planets.length} planets · ${def.spawnPoints.length} spawn points · ${def.size.w} × ${def.size.h}`;
		};
		const first = maps[0];
		if (first) show(first.def);

		return menuScreen({
			title: "Choose map",
			subtitle,
			className: "map-choice",
			content: el("div", { class: "map-preview-box" }, canvas, info),
			back: () => this.ui.pop(),
			items: maps.map((m) => ({
				label: m.label || m.def.name,
				onFocus: () => show(m.def),
				onSelect: () => this.ui.push(this.rulesChoice({ ...base, ...m.settings }, start, subtitle)),
			})),
		});
	}

	private rulesChoice(
		match: MatchSettings,
		start: (settings: MatchSettings) => void,
		subtitle: string,
	): Screen {
		const summary = (rules: MatchRules) =>
			RULE_KEYS.filter((k) => rules[k])
				.map((k) => RULE_TEXT[k].label)
				.join(", ");
		return menuScreen({
			title: "Rules",
			subtitle,
			back: () => this.ui.pop(),
			items: [
				{
					label: "Modern",
					hint: `Every mechanic on with tuned defaults: ${summary(MODERN_RULES)}.`,
					onSelect: () => start({ ...match, rules: cloneRules(MODERN_RULES) }),
				},
				{
					label: "Classic",
					hint: "Exactly the 2008 game: one hit kills, unlimited fuel, lasers and missiles.",
					onSelect: () => start({ ...match, rules: cloneRules(CLASSIC_RULES) }),
				},
				{
					label: "Custom…",
					hint: `Set every option yourself (last used: ${summary(loadSettings().customRules) || "nothing switched on"}).`,
					onSelect: () => this.ui.push(this.customRules(match, start)),
				},
			],
		});
	}

	private customRules(match: MatchSettings, start: (settings: MatchSettings) => void): Screen {
		const map = this.mapOf(match);
		const editor = rulesEditor({
			rules: loadSettings().customRules,
			maxLaserRange: 2 * Math.max(map.size.w, map.size.h),
			onChange: (rules) => saveSettings({ ...loadSettings(), customRules: rules }),
			onStart: (rules) => start({ ...match, rules }),
		});
		return pageScreen("Custom rules", editor, () => this.ui.pop(), "custom-rules-screen");
	}

	private previewImages(): PreviewImages {
		const keys: ImageKey[] = ["background", "planetMars", "planetEarth", "planetPluto"];
		const images: PreviewImages = {};
		for (const key of keys) {
			if (this.game.textures.exists(key)) {
				images[key] = this.game.textures.get(key).getSourceImage() as CanvasImageSource;
			}
		}
		return images;
	}

	private helpScreen(): Screen {
		return pageScreen(
			"Help & Controls",
			helpContent(loadSettings().bindings),
			() => this.ui.pop(),
			"help-screen",
		);
	}

	/**
	 * Leaving a running match asks first (P11.10, 2008 req. 4.1.7). "Keep playing" is focused,
	 * so a stray Enter doesn't end the match; Esc goes back to the pause menu.
	 */
	private confirmLeave(title: string, subtitle: string, confirm: string): Screen {
		const screen = menuScreen({
			title,
			subtitle,
			className: "pause-menu confirm-leave",
			back: () => this.ui.remove(screen),
			items: [
				{ label: "Keep playing", onSelect: () => this.ui.remove(screen) },
				{ label: confirm, onSelect: () => this.showMainMenu() },
			],
		});
		return screen;
	}

	private settingsScreen(): Screen {
		const content = settingsContent({
			settings: loadSettings(),
			ui: this.ui,
			onChange: (settings: Settings) => {
				saveSettings(settings);
				this.game.sound.volume = settings.volume;
				this.game.sound.mute = settings.muted;
				setResolutionMode(settings.resolution);
				setMotionPreference(settings.reducedMotion);
				this.current?.applySettings(settings);
			},
			toggleFullscreen: () => toggleFullscreen(this.game),
			playTestSound: () => {
				if (this.game.cache.audio.exists("boing")) this.game.sound.play("boing");
			},
		});
		return pageScreen("Settings", content, () => this.ui.pop(), "settings-screen");
	}

	private showPauseMenu(): void {
		if (this.pauseScreen) return;
		const resume = () => this.current?.setPaused(false);
		if (this.lastMatch.replay) {
			const replay = this.lastMatch.replay;
			this.pauseScreen = menuScreen({
				title: "Replay paused",
				className: "pause-menu",
				back: resume,
				items: [
					{ label: "Resume", onSelect: resume },
					{
						label: "Watch from the start",
						onSelect: () =>
							this.startMatch({ ...this.lastMatch, replay: { inputs: replay.inputs } }),
					},
					{ label: "Replays", onSelect: () => this.showReplays() },
					{ label: "Quit to menu", onSelect: () => this.showMainMenu() },
				],
			});
			this.ui.push(this.withVersion(this.pauseScreen));
			document.body.dataset.paused = "true";
			return;
		}
		if (this.lastMatch.online) {
			const online = this.lastMatch.online;
			const report =
				online.desyncTick === null
					? []
					: [
							{
								label: "Save desync report",
								hint: "The matches went out of sync. The report lets the bug be reproduced (pnpm desync).",
								onSelect: () => {
									const data = online.desyncReport(__APP_VERSION__);
									if (data)
										downloadJson(`gravity-desync-${online.setup.code}-${data.tick}.json`, data);
								},
							},
						];
			// Online: the match keeps running behind this menu.
			this.pauseScreen = menuScreen({
				title: "Menu",
				subtitle: "The match keeps running",
				className: "pause-menu",
				back: resume,
				items: [
					{ label: "Resume", onSelect: resume },
					...report,
					{ label: "Help & Controls", onSelect: () => this.ui.push(this.helpScreen()) },
					{ label: "Settings", onSelect: () => this.ui.push(this.settingsScreen()) },
					{
						label: "Leave match",
						onSelect: () => this.ui.push(this.quitConfirm()),
					},
				],
			});
			this.ui.push(this.withVersion(this.pauseScreen));
			document.body.dataset.paused = "true";
			return;
		}
		this.pauseScreen = menuScreen({
			title: "Paused",
			className: "pause-menu",
			back: resume,
			items: [
				{ label: "Resume", onSelect: resume },
				{ label: "Restart", onSelect: () => this.startMatch(this.lastMatch) },
				{ label: "Help & Controls", onSelect: () => this.ui.push(this.helpScreen()) },
				{ label: "Settings", onSelect: () => this.ui.push(this.settingsScreen()) },
				{
					label: "Quit to menu",
					onSelect: () => this.ui.push(this.quitConfirm()),
				},
			],
		});
		this.ui.push(this.withVersion(this.pauseScreen));
		document.body.dataset.paused = "true";
	}

	private showGameOver(result: MatchResult, world: WorldState): void {
		this.hidePauseMenu();
		const online = this.lastMatch.online;
		if (online) this.online.finished();
		this.lastNames = this.current?.pilotNames() ?? [];
		this.lastReplay = this.makeReplay(world);
		if (this.lastReplay) storeReplay(this.lastReplay);
		const saveReplay = {
			label: "Save replay",
			onSelect: () => {
				if (this.lastReplay) downloadReplay(this.lastReplay);
			},
		};
		const aiPlayers = (this.lastMatch.ai ?? []).map((a) => a.player);
		const names = this.lastNames;
		const name = (i: number) =>
			`${pilotLabel(world.players[i]?.color ?? "blue", names[i])}${aiPlayers.includes(i) ? " (AI)" : ""}`;
		const title = online
			? result.kind === "winner"
				? result.winner === online.slot
					? "You win"
					: "You lose"
				: "Draw"
			: result.kind === "winner" && aiPlayers.length > 0
				? aiPlayers.includes(result.winner)
					? "The computer wins"
					: "You win"
				: result.kind === "winner"
					? `${name(result.winner)} wins`
					: result.kind === "draw"
						? "Draw"
						: "Game over";
		const board = highscoreBoard(
			world.objective?.kind === "survival",
			world.rules.safeAsteroidSpawns,
		);
		const subtitle =
			result.kind === "solo"
				? isScoreBoard(board)
					? `Score: ${world.objective?.scores[0] ?? 0} · asteroids: ${world.players[0]?.asteroidKills ?? 0}`
					: `Asteroids destroyed: ${world.players[0]?.asteroidKills ?? 0}`
				: world.objective && world.objective.kind !== "survival"
					? `First to ${world.objective.target} · match length ${formatTicks(world.tick)}`
					: `Match length ${formatTicks(world.tick)}`;

		const rows = world.players.map((p) =>
			el(
				"tr",
				{ class: `row-${p.color}` },
				el("th", { scope: "row" }, name(p.index)),
				el(
					"td",
					{},
					String(
						world.objective && world.objective.kind !== "survival"
							? (world.objective.scores[p.index] ?? 0)
							: livesOf(p),
					),
				),
				el("td", {}, String(p.shipKills)),
				el("td", {}, String(p.asteroidKills)),
			),
		);
		const table = el(
			"table",
			{ class: "stats-table" },
			el(
				"thead",
				{},
				el(
					"tr",
					{},
					el("th", {}, "Pilot"),
					el(
						"th",
						{},
						world.objective && world.objective.kind !== "survival" ? "Score" : "Ships left",
					),
					el("th", {}, "Kills"),
					el("th", {}, "Asteroids"),
				),
			),
			el("tbody", {}, ...rows),
		);

		const score = isScoreBoard(board)
			? (world.objective?.scores[0] ?? 0)
			: (world.players[0]?.asteroidKills ?? 0);
		const survival = world.mode === "survival";
		const newRecord = survival && qualifies(loadHighscores(board), score);
		const seconds = (world.tick * TICK_MS) / 1000;
		const again = survival ? "Play again" : "Rematch";

		let nameInput: HTMLInputElement | null = null;
		const saveScore = () => {
			const name = cleanName(nameInput?.value ?? "");
			saveLastName(name);
			const { list, rank } = insertEntry(loadHighscores(board), {
				name,
				score,
				map: this.mapName(),
				rules: rulesLabel(this.lastMatch.rules),
				seconds,
				date: new Date().toISOString(),
			});
			saveHighscores(list, board);
			this.showHighscoresAfterRun(rank, board);
		};

		let content: HTMLElement = table;
		if (newRecord) {
			nameInput = el("input", {
				type: "text",
				id: "highscore-name",
				maxlength: NAME_MAX_LENGTH,
				value: this.lastNames[0] || loadLastName(),
				autocomplete: "off",
				"data-autofocus": true,
			});
			nameInput.addEventListener("keydown", (ev) => {
				if (ev.key === "Enter") {
					ev.preventDefault();
					// Otherwise the same Enter reaches the ScreenManager and "clicks" whatever the
					// next screen focuses (e.g. Play again).
					ev.stopPropagation();
					saveScore();
				}
			});
			content = el(
				"div",
				{},
				table,
				el(
					"div",
					{ class: "name-entry" },
					el("label", { for: "highscore-name" }, "New highscore! Your name"),
					nameInput,
				),
			);
		}

		const items = online
			? [
					{ label: REMATCH, onSelect: () => {} }, // wired by OnlineFlow.attachGameOver
					// Only the host picks the next match's map, rules and mode (I-013).
					...(this.online.current?.isHost
						? [
								{
									label: CHANGE_REMATCH,
									hint: "Pick another mode, map or rules for the rematch; your opponent accepts it.",
									onSelect: () => {}, // wired by OnlineFlow.attachGameOver
								},
							]
						: []),
					saveReplay,
					{ label: "Main menu", onSelect: () => this.showMainMenu() },
				]
			: newRecord
				? [
						{ label: "Save score", onSelect: saveScore },
						{ label: again, onSelect: () => this.startMatch(this.lastMatch) },
						saveReplay,
						{ label: "Main menu", onSelect: () => this.showMainMenu() },
					]
				: [
						{ label: again, onSelect: () => this.startMatch(this.lastMatch) },
						saveReplay,
						{ label: "Main menu", onSelect: () => this.showMainMenu() },
					];

		const screen = menuScreen({
			title,
			subtitle,
			className: "game-over",
			content,
			back: () => this.showMainMenu(),
			items,
		});
		this.ui.reset(screen);
		if (online) this.online.attachGameOver(screen.el);
		if (nameInput) {
			nameInput.focus();
			nameInput.select();
		}
		document.body.dataset.screen = "game-over";
	}

	/** The finished match as a replay (null if the scene is gone). */
	private makeReplay(world: WorldState): Replay | null {
		const scene = this.current;
		if (!scene) return null;
		const rec = scene.recording();
		const online = this.lastMatch.online;
		const aiPlayers = (this.lastMatch.ai ?? []).map((a) => a.player);
		const names = this.lastNames;
		const pilots = world.players.map((p) => {
			const color = pilotLabel(p.color, names[p.index]);
			if (aiPlayers.includes(p.index)) return `${color} (AI)`;
			if (online && p.index === online.slot) return `${color} (you)`;
			return color;
		});
		const kind: Replay["kind"] = online
			? "online"
			: rec.mode === "survival"
				? "survival"
				: aiPlayers.length > 0
					? "ai"
					: "local";
		return {
			format: "gravity-replay",
			sim: SIM_VERSION,
			app: __APP_VERSION__,
			date: new Date().toISOString(),
			map: rec.map,
			rules: rec.rules,
			mode: rec.mode,
			seed: rec.seed,
			pilots,
			kind,
			ticks: Math.min(...rec.inputs.map((i) => i.length)),
			inputs: rec.inputs.map(encodeInputs),
			...(rec.customMap ? { customMap: rec.customMap } : {}),
			...(this.lastMatch.survivalScoring === "2008" ? { survivalScoring: "2008" as const } : {}),
			...(names.some(Boolean) ? { names: world.players.map((p) => names[p.index] ?? "") } : {}),
		};
	}

	/** Plays a replay (inputs already decoded). */
	private watch(replay: Replay, inputs: number[][]): void {
		this.startMatch({
			map: replay.map,
			...(replay.customMap ? { customMap: replay.customMap } : {}),
			...(replay.survivalScoring ? { survivalScoring: replay.survivalScoring } : {}),
			rules: replay.rules,
			seed: replay.seed,
			mode: replay.mode,
			playerCount: inputs.length as 1 | 2 | 3 | 4,
			replay: { inputs },
			...(replay.names ? { names: replay.names } : {}),
		});
		document.body.dataset.screen = "replay";
	}

	/** Map editor home (P11.1): the custom maps in this browser. */
	private mapsScreen(): Screen {
		const maps = loadCustomMaps();
		const refresh = () => {
			this.ui.remove(screen);
			this.ui.push(this.mapsScreen());
		};
		const small = (text: string, onClick: () => void) =>
			el("button", { class: "btn btn-small", type: "button", onclick: onClick }, text);
		const rows = maps.map((m) =>
			el(
				"li",
				{ class: "replay-row" },
				el(
					"div",
					{ class: "replay-info" },
					el("strong", {}, m.map.name),
					el(
						"span",
						{},
						`${m.map.planets.length} planets · ${m.map.spawnPoints.length} spawn points · ${m.map.size.w} × ${m.map.size.h}`,
					),
				),
				small("Edit", () => this.ui.push(this.editorScreen(m))),
				small("Duplicate", () => {
					saveCustomMap({
						id: newMapId(),
						updated: new Date().toISOString(),
						map: { ...structuredClone(m.map), name: `${m.map.name} copy`.slice(0, 32) },
					});
					refresh();
				}),
				small("Save file", () => downloadText(mapFileName(m.map), exportMap(m.map))),
				small("Delete", () => {
					deleteCustomMap(m.id);
					refresh();
				}),
			),
		);
		const file = el("input", {
			type: "file",
			accept: ".json,application/json",
			class: "replay-file",
		});
		file.addEventListener("change", () => {
			const picked = file.files?.[0];
			file.value = "";
			if (!picked) return;
			void picked.text().then((text) => {
				const result = importMap(text);
				if ("error" in result) {
					this.ui.push(this.notice("Can't open this map", result.error));
					return;
				}
				saveCustomMap({ id: newMapId(), updated: new Date().toISOString(), map: result.map });
				refresh();
			});
		});
		const content = el(
			"div",
			{ class: "replays" },
			rows.length > 0
				? el("ul", { class: "replay-list" }, ...rows)
				: el("p", { class: "page-text" }, "No maps yet. Start a new one, or open a map file."),
			el(
				"div",
				{ class: "replay-actions" },
				small("New map", () =>
					this.ui.push(this.editorScreen({ id: newMapId(), updated: "", map: blankMap() })),
				),
				small("Open map file…", () => file.click()),
				file,
			),
			el(
				"p",
				{ class: "page-text" },
				`Your maps (up to ${MAX_CUSTOM_MAPS}) are kept in this browser and appear with ★ in every map choice.`,
			),
		);
		const screen = pageScreen("Map editor", content, () => this.ui.pop(), "maps-screen");
		return screen;
	}

	private editorScreen(entry: StoredMap): Screen {
		let current = entry.map;
		let saved = entry.updated !== "";
		const store = (map: MapDef): boolean => {
			const ok = saveCustomMap({ id: entry.id, updated: new Date().toISOString(), map });
			saved = ok;
			if (!ok)
				this.ui.push(
					this.notice(
						"Not saved",
						"The browser refused to store the map (storage full or blocked).",
					),
				);
			return ok;
		};
		const editor = mapEditor({
			map: entry.map,
			images: this.previewImages(),
			onChange: (map) => {
				current = map;
				saved = false;
			},
			onSave: (map) => {
				if (store(map))
					this.ui.push(this.notice("Saved", `"${map.name}" is in the map choice of every mode.`));
			},
			onTestFly: (map) => {
				if (!store(map)) return;
				this.startMatch({
					playerCount: 2,
					mode: "duel",
					customMap: map,
					ai: [{ player: 1, difficulty: "easy" }],
					rules: cloneRules(MODERN_RULES),
				});
			},
			onExport: (map) => downloadText(mapFileName(map), exportMap(map)),
		});
		const leave = () => {
			this.ui.remove(screen);
			this.ui.remove(maps);
			this.ui.push(this.mapsScreen());
		};
		const maps = this.ui.top as Screen;
		const back = () => {
			if (saved) {
				leave();
				return;
			}
			const valid = customMapProblems(current).length === 0;
			const ask: Screen = menuScreen({
				title: "Unsaved changes",
				subtitle: current.name,
				back: () => this.ui.remove(ask),
				items: [
					{ label: "Keep editing", onSelect: () => this.ui.remove(ask) },
					{
						label: "Save and leave",
						disabled: !valid,
						hint: valid ? "" : "Fix the listed problems first.",
						onSelect: () => {
							this.ui.remove(ask);
							if (store(current)) leave();
						},
					},
					{
						label: "Discard changes",
						onSelect: () => {
							this.ui.remove(ask);
							leave();
						},
					},
				],
			});
			this.ui.push(ask);
		};
		const screen = pageScreen("Edit map", editor, back, "map-editor-screen");
		return screen;
	}

	private replaysScreen(): Screen {
		const replays = loadReplays();
		const screen = pageScreen(
			"Replays",
			replaysContent({
				replays,
				mapName: (r) => this.mapOf(r).name,
				onWatch: (i) => {
					const parsed = replays[i] ? parseReplay(replays[i]) : null;
					if (parsed && "replay" in parsed) this.watch(parsed.replay, parsed.inputs);
				},
				onDownload: (i) => {
					const replay = replays[i];
					if (replay) downloadReplay(replay);
				},
				onDelete: (i) => {
					deleteReplay(i);
					this.ui.remove(screen);
					this.ui.push(this.replaysScreen());
				},
				onOpenFile: (text) => {
					let raw: unknown = null;
					try {
						raw = JSON.parse(text);
					} catch {
						raw = null;
					}
					const parsed = parseReplay(raw);
					if ("replay" in parsed) this.watch(parsed.replay, parsed.inputs);
					else this.ui.push(this.notice("Can't play this replay", parsed.error));
				},
			}),
			() => this.ui.pop(),
			"replays-screen",
		);
		return screen;
	}

	/** Back to the Replays page from a replay (menus underneath are rebuilt). */
	private showReplays(): void {
		this.showMainMenu();
		this.ui.push(this.replaysScreen());
	}

	private showReplayEnd(): void {
		const replay = this.lastMatch.replay;
		this.ui.reset(
			menuScreen({
				title: "Replay finished",
				className: "replay-end",
				back: () => this.showMainMenu(),
				items: [
					{
						label: "Watch again",
						onSelect: () => {
							if (replay) this.startMatch({ ...this.lastMatch, replay: { inputs: replay.inputs } });
							document.body.dataset.screen = "replay";
						},
					},
					{ label: "Replays", onSelect: () => this.showReplays() },
					{ label: "Main menu", onSelect: () => this.showMainMenu() },
				],
			}),
		);
		document.body.dataset.screen = "replay-end";
	}

	private notice(title: string, text: string): Screen {
		const screen: Screen = menuScreen({
			title,
			content: el("p", { class: "online-text" }, text),
			back: () => this.ui.remove(screen),
			items: [{ label: "Back", onSelect: () => this.ui.remove(screen) }],
		});
		return screen;
	}

	private showHighscoresAfterRun(rank: number, board: HighscoreBoard): void {
		const label = boardLabel(board);
		this.ui.reset(
			menuScreen({
				title: "Highscores",
				subtitle: rank >= 0 ? `You placed #${rank + 1} · ${label}` : label,
				className: "highscores",
				content: highscoreTable(
					loadHighscores(board),
					rank,
					isScoreBoard(board) ? "Score" : "Asteroids",
				),
				back: () => this.showMainMenu(),
				items: [
					{ label: "Play again", onSelect: () => this.startMatch(this.lastMatch) },
					{ label: "Main menu", onSelect: () => this.showMainMenu() },
				],
			}),
		);
		document.body.dataset.screen = "highscores";
	}

	/** The map a match is played on: its custom map, or the built-in one. */
	private mapOf(match: { map?: MapKey; customMap?: MapDef }): MapDef {
		if (match.customMap) return match.customMap;
		const key = match.map ?? "classic";
		return loadMap(this.game.cache.text.get(key) as string, MAPS[key]);
	}

	private mapName(): string {
		return this.mapOf(this.lastMatch).name;
	}

	private hidePauseMenu(): void {
		if (this.pauseScreen) this.ui.remove(this.pauseScreen);
		this.pauseScreen = null;
		document.body.dataset.paused = "false";
	}
}

/** Offers a replay as a JSON file download. */
function downloadReplay(replay: Replay): void {
	downloadJson(replayFileName(replay), replay);
}

/** Offers data as a JSON file download. */
function downloadJson(name: string, data: unknown): void {
	downloadText(name, JSON.stringify(data));
}

/** Offers text (JSON) as a file download. */
function downloadText(name: string, text: string): void {
	const blob = new Blob([text], { type: "application/json" });
	const url = URL.createObjectURL(blob);
	const a = el("a", { href: url, download: name });
	document.body.append(a);
	a.click();
	a.remove();
	setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Formats a tick count as m:ss of game time. */
function formatTicks(ticks: number): string {
	const seconds = Math.floor((ticks * TICK_MS) / 1000);
	return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

function describeMode(match: MatchSettings): string {
	if (match.mode === "survival") {
		return match.survivalScoring === "2008" ? "Survival · 2008 scoring" : "Asteroid survival";
	}
	const objective =
		match.mode === "koth"
			? " · King of the Hill"
			: match.mode === "ctf"
				? " · Capture the Flag"
				: "";
	const ai = match.ai?.[0];
	if (ai && (match.playerCount ?? 2) <= 2) return `Versus computer (${ai.difficulty})${objective}`;
	const who =
		match.playerCount === 1
			? "Single player"
			: match.playerCount === 2
				? "Two players"
				: `Party · ${match.playerCount} pilots`;
	return `${who}${objective}`;
}

/** Short name of a rule set for the highscore table. */
function rulesLabel(rules: MatchRules | undefined): string {
	if (!rules) return "Classic";
	if (sameRules(rules, CLASSIC_RULES)) return "Classic";
	if (sameRules(rules, MODERN_RULES)) return "Modern";
	return "Custom";
}
